import React from 'react';
import JiraKeyChip from '../JiraKeyChip';
import StatusPill from './StatusPill';
import { ReworkData } from './types';
import { ArrowRight } from 'lucide-react';
import * as fmt from '../../lib/format';

interface Props {
  data: ReworkData | null;
  loading: boolean;
  emptyText: string;
}

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const CAPTION = 'text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500';

// Work that came back: moved into Reopened, Change Requested or Issue Reported, or back to an
// earlier step. Shown per status pair and project, never per person.
export default function ReworkView({ data, loading, emptyText }: Props) {
  if (loading && !data) {
    return (
      <div className={`${CARD} animate-pulse p-5 motion-reduce:animate-none`} role="status" aria-label="Loading the tickets sent back">
        <div className="h-3 w-24 rounded bg-gray-100" />
        <div className="mt-3 h-8 w-20 rounded bg-gray-100" />
        <div className="mt-6 h-3 w-2/3 rounded bg-gray-100" />
      </div>
    );
  }
  if (!data || data.moves === 0) {
    return <div className={`${CARD} px-5 py-14 text-center text-sm text-gray-500`}>{emptyText}</div>;
  }
  const maxPair = Math.max(1, ...data.by_pair.map((p) => p.count));

  return (
    <div className="space-y-5">
      <section className={`${CARD} p-5`} aria-label="How many moves went back">
        <h3 className={CAPTION}>Came back</h3>
        <div className="mt-1 flex items-baseline gap-3">
          <span className="text-3xl font-semibold tabular-nums tracking-tight text-gray-900">
            {fmt.percent(data.rework_rate_pct)}
          </span>
          <span className="text-sm text-gray-600">
            of moves went back: {fmt.count(data.rework)} of {fmt.count(data.moves)}
          </span>
        </div>
      </section>

      {data.by_pair.length > 0 && (
        <section className={`${CARD} p-5`} aria-label="Where work came back from">
          <h3 className={CAPTION}>Where it came back from</h3>
          <ul className="mt-3 space-y-3">
            {data.by_pair.map((p) => (
              <li key={`${p.from}|${p.to}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <StatusPill status={p.from} />
                    <ArrowRight className="h-3.5 w-3.5 text-gray-500" aria-hidden />
                    <StatusPill status={p.to} />
                    <span className="text-xs text-gray-500">{p.kind === 'bounce' ? 'sent back' : 'stepped back'}</span>
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-gray-900">{p.count}</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100" aria-hidden>
                  <div className="h-full rounded-full bg-rose-400" style={{ width: `${(p.count / maxPair) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {data.by_project.length > 0 && (
          <section className={`${CARD} p-5`} aria-label="Sent back, by project">
            <h3 className={CAPTION}>By project</h3>
            <ul className="mt-3 divide-y divide-gray-100">
              {data.by_project.slice(0, 10).map((p) => (
                <li key={p.project_key} className="flex items-baseline justify-between gap-3 py-2">
                  <span className="min-w-0 truncate text-sm text-gray-900">
                    <span className="font-semibold">{p.project_key}</span>
                    {p.project_name && <span className="ml-2 text-gray-500">{p.project_name}</span>}
                  </span>
                  <span className="shrink-0 text-sm tabular-nums text-gray-600">
                    <span className="font-semibold text-gray-900">{p.rework}</span>
                    {p.rework_rate_pct !== null ? ` · ${fmt.percent(p.rework_rate_pct)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
        {data.tickets.length > 0 && (
          <section className={`${CARD} p-5`} aria-label="Tickets that came back">
            <h3 className={CAPTION}>Tickets that came back</h3>
            <ul className="mt-3 divide-y divide-gray-100">
              {data.tickets.map((t) => (
                <li key={t.issue_key} className="flex items-start justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <JiraKeyChip issueKey={t.issue_key} url={t.jira_url} />
                    <span className="mt-1 line-clamp-1 block text-sm text-gray-700" title={t.issue_name ?? undefined}>
                      {t.issue_name || 'Untitled'}
                    </span>
                  </span>
                  {t.count > 1 && <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">{t.count}×</span>}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
